import { Modal, Typography } from "antd";
import { useState, type FC } from "react";
import { TargetPicker } from "../../../components";
import type { Project, SnippetDeploymentTarget } from "../../../types";
import type { SnippetRow } from "../types";

const { Text } = Typography;

interface DeployModalProps {
  deployModal: { snippetIds: string[] } | null;
  rows: SnippetRow[];
  projects: Project[];
  targetsBySnippet: Record<string, SnippetDeploymentTarget[]>;
  deploying: boolean;
  onCancel: () => void;
  onConfirm: (environmentIds: string[]) => void;
}

export const DeployModal: FC<DeployModalProps> = ({
  deployModal,
  rows,
  projects,
  targetsBySnippet,
  deploying,
  onCancel,
  onConfirm,
}) => {
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<string[]>([]);

  // Reset the selection whenever a new deploy target is opened. Adjusting state during
  // render (React's documented alternative to a reset-on-prop-change effect) instead of an
  // effect, so this doesn't fire an extra render pass after the modal is already visible.
  const [lastDeployModal, setLastDeployModal] = useState(deployModal);
  if (deployModal !== lastDeployModal) {
    setLastDeployModal(deployModal);
    setSelectedEnvironmentIds([]);
  }

  // A 'site' project may only deploy to its own environments, a 'library' can deploy anywhere
  // (see the design doc, "Portée de déploiement selon kind"). When every snippet in this batch
  // comes from the same 'site' project, narrow the picker to that project so it doesn't offer
  // targets the api would reject. A mixed batch (e.g. bulk "Deploy all" across several projects)
  // falls back to the full list; any pair that's still invalid for a given snippet surfaces as a
  // per-target failure from the api instead (see DeployResultsModal).
  const sourceProjectIds = new Set(
    (deployModal?.snippetIds ?? []).map((id) => rows.find((r) => r.id === id)?.projectId)
  );
  const singleSourceProject =
    sourceProjectIds.size === 1 ? projects.find((p) => p.id === [...sourceProjectIds][0]) : undefined;
  const restrictedToOwnProject = singleSourceProject?.kind === "site";
  const pickerProjects =
    singleSourceProject && restrictedToOwnProject ? [singleSourceProject] : projects;

  return (
    <Modal
      title={
        deployModal && deployModal.snippetIds.length === 1
          ? `Deploy "${rows.find((r) => r.id === deployModal.snippetIds[0])?.name ?? ""}" to`
          : `Deploy ${deployModal?.snippetIds.length ?? 0} snippets to`
      }
      open={Boolean(deployModal)}
      onCancel={onCancel}
      onOk={() => onConfirm(selectedEnvironmentIds)}
      confirmLoading={deploying}
      okButtonProps={{ disabled: selectedEnvironmentIds.length === 0 }}
    >
      {deployModal && deployModal.snippetIds.length > 1 && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {deployModal.snippetIds.map((id) => rows.find((r) => r.id === id)?.name ?? id).join(", ")}
        </Text>
      )}
      {restrictedToOwnProject && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          This is a site project, it can only deploy to its own environments. Use a library
          project to share a snippet across projects.
        </Text>
      )}
      <TargetPicker
        projects={pickerProjects}
        value={selectedEnvironmentIds}
        onChange={setSelectedEnvironmentIds}
        deployedTargets={
          deployModal && deployModal.snippetIds.length === 1
            ? targetsBySnippet[deployModal.snippetIds[0]]
            : undefined
        }
      />
    </Modal>
  );
};
